import React, { createContext, useCallback, useContext } from 'react';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { usePersistedState } from '../hooks/usePersistedState';
import { LOCAL_STORAGE_KEYS } from '../constants/localStorage';
import { HelpPanelState, isHelpPanelState } from '../interfaces/persistedState';

interface HelpPanelContextType {
    state: {
        toolsOpen: boolean;
        helpPanelTopic: string;
    };
    actions: {
        setHelpPanelTopic(helpPanelTopic: string): void;
        setToolsOpen(toolsOpen: boolean): void;
        makeHelpPanelHandler(topic: string): () => void;
    };
}

const HelpPanelContext = createContext<HelpPanelContextType | undefined>(
    undefined,
);

export function useHelpPanel() {
    const context = useContext(HelpPanelContext);
    if (!context) {
        throw new Error('useHelpPanel must be used within a HelpPanelProvider');
    }

    const canShowHelpPanel = useFeatureFlag('enable-help-panel');
    const state = {
        ...context.state,
        helpPanelTopic: canShowHelpPanel ? context.state.helpPanelTopic : null,
    };

    return {
        state,
        actions: context.actions,
        canShowHelpPanel,
    };
}

export function HelpPanelProvider({ children }: { children: React.ReactNode }) {
    // Use centralized persisted state for help panel
    const [helpPanelState, setHelpPanelState] =
        usePersistedState<HelpPanelState>(
            { toolsOpen: true, helpPanelTopic: null },
            {
                key: LOCAL_STORAGE_KEYS.HELP_PANEL_STATE,
                defaultValue: { toolsOpen: true, helpPanelTopic: null },
                validator: isHelpPanelState,
            },
        );

    const setHelpPanelTopic = useCallback(
        (topic: string) => {
            setHelpPanelState((prevState) => ({
                ...prevState,
                helpPanelTopic: topic,
            }));
        },
        [setHelpPanelState],
    );

    const setToolsOpen = useCallback(
        (isOpen: boolean) => {
            setHelpPanelState((prevState) => ({
                ...prevState,
                toolsOpen: isOpen,
            }));
        },
        [setHelpPanelState],
    );

    const makeHelpPanelHandler = useCallback(
        (topic: string) => () => {
            setHelpPanelTopic(topic);
            setToolsOpen(true);
        },
        [setHelpPanelTopic, setToolsOpen],
    );

    const state = {
        helpPanelTopic: helpPanelState.helpPanelTopic,
        toolsOpen: helpPanelState.toolsOpen,
    };

    const actions = {
        setHelpPanelTopic,
        setToolsOpen,
        makeHelpPanelHandler,
    };

    return (
        <HelpPanelContext.Provider value={{ state, actions }}>
            {children}
        </HelpPanelContext.Provider>
    );
}
